/**
 * Single-page confirmation modal shown after a booking is created.
 *
 * Hero checkmark + reference code at the top, then a compact card of the
 * key facts (date, time, master, address), then share + new-booking
 * actions. Mirrors the older `TicketSuccessModal` but rebuilt around
 * the salon flow's data shape (cart of services, optional master, etc.).
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getMasterById } from "./mockSalonData";
import { formatDuration, formatPrice, pickLocalized } from "./salonTheme";

const BACKDROP_BG = "rgba(15, 23, 42, 0.55)";

export function SalonSuccessModal({
  palette,
  lang,
  isOpen,
  onClose,
  booking,
  cart,
  totalDuration,
  totalPrice,
  hasOpenPriceFrom,
  slot,
  masterId,
  salon,
  onNewBooking,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const master = masterId ? getMasterById(masterId) : null;
  const priceLabel = hasOpenPriceFrom
    ? `${t("salonBooking.service.from")} ${formatPrice(totalPrice, "AMD", lang)}`
    : formatPrice(totalPrice, "AMD", lang);

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: BACKDROP_BG,
        zIndex: 1090,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(12px, 4vw, 28px)",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: palette.surface,
          border: palette.border,
          borderRadius: "clamp(18px, 3vw, 22px)",
          boxShadow: "0 30px 60px rgba(15, 23, 42, 0.3)",
          width: "100%",
          maxWidth: 520,
          padding: "clamp(22px, 5vw, 36px) clamp(20px, 5vw, 32px)",
          display: "grid",
          gap: "clamp(14px, 3vw, 18px)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("salonBooking.success.close")}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: palette.border,
            background: palette.surfaceAlt,
            color: palette.textMuted,
            cursor: "pointer",
          }}
        >
          <i className="las la-times" aria-hidden="true" />
        </button>

        {/* Hero — checkmark */}
        <div className="d-flex flex-column align-items-center text-center" style={{ gap: 10 }}>
          <span
            style={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              background: palette.success,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2.4rem",
              boxShadow: `0 12px 30px ${palette.successSoft}`,
            }}
            aria-hidden="true"
          >
            <i className="las la-check" />
          </span>
          <h3
            className="fw-700 mb-0"
            style={{
              color: palette.text,
              fontSize: "clamp(1.1rem, 3vw, 1.35rem)",
              lineHeight: 1.3,
            }}
          >
            {t("salonBooking.success.title")}
          </h3>
          <p
            className="mb-0"
            style={{
              color: palette.textMuted,
              fontSize: "0.95rem",
              maxWidth: 360,
              lineHeight: 1.4,
            }}
          >
            {t("salonBooking.success.subtitle", {
              salon: salon ? pickLocalized(salon.name, lang) : "",
            })}
          </p>
        </div>

        {/* Reference code */}
        {booking?.reference ? (
          <div
            style={{
              background: palette.accentSoft,
              border: `2px dashed ${palette.accentBorder}`,
              borderRadius: 16,
              padding: "14px 16px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                color: palette.accent,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
              }}
            >
              {t("salonBooking.success.refLabel")}
            </div>
            <div
              className="fw-700"
              style={{
                color: palette.accent,
                fontSize: "clamp(1.6rem, 5vw, 2.2rem)",
                letterSpacing: "0.06em",
                fontVariantNumeric: "tabular-nums",
                marginTop: 4,
              }}
            >
              {booking.reference}
            </div>
          </div>
        ) : null}

        {/* Compact card */}
        <div
          style={{
            background: palette.surfaceAlt,
            border: palette.border,
            borderRadius: 14,
            padding: "12px 14px",
            display: "grid",
            gap: 8,
          }}
        >
          {slot ? (
            <div className="d-flex align-items-center gap-2">
              <i
                className="las la-calendar"
                style={{ color: palette.accent, fontSize: "1.1rem" }}
                aria-hidden="true"
              />
              <span style={{ color: palette.text, fontSize: "0.95rem" }}>
                {slot.date} • <strong>{slot.time}</strong>
              </span>
            </div>
          ) : null}
          {master ? (
            <div className="d-flex align-items-center gap-2">
              <i
                className="las la-user"
                style={{ color: palette.accent, fontSize: "1.1rem" }}
                aria-hidden="true"
              />
              <span style={{ color: palette.text, fontSize: "0.95rem" }}>
                {pickLocalized(master.name, lang)} • {pickLocalized(master.title, lang)}
              </span>
            </div>
          ) : null}
          {salon?.address ? (
            <div className="d-flex align-items-center gap-2">
              <i
                className="las la-map-marker-alt"
                style={{ color: palette.accent, fontSize: "1.1rem" }}
                aria-hidden="true"
              />
              <span style={{ color: palette.text, fontSize: "0.95rem" }}>
                {pickLocalized(salon.address, lang)}
              </span>
            </div>
          ) : null}
          <div className="d-flex align-items-center gap-2">
            <i
              className="las la-list"
              style={{ color: palette.accent, fontSize: "1.1rem" }}
              aria-hidden="true"
            />
            <span style={{ color: palette.text, fontSize: "0.95rem" }}>
              {cart.length > 0
                ? cart.map((s) => pickLocalized(s.name, lang)).join(", ")
                : "—"}
            </span>
          </div>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              borderTop: palette.border,
              paddingTop: 8,
              marginTop: 4,
            }}
          >
            <span style={{ color: palette.textMuted, fontSize: "0.85rem" }}>
              <i className="las la-clock me-1" aria-hidden="true" />
              {formatDuration(totalDuration, lang)}
            </span>
            <span
              className="fw-700"
              style={{
                color: palette.text,
                fontSize: "1.05rem",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {priceLabel}
            </span>
          </div>
        </div>

        {/* Notice */}
        <div
          style={{
            background: palette.surfaceAlt,
            border: palette.border,
            borderRadius: 12,
            padding: "10px 14px",
            color: palette.textMuted,
            fontSize: "0.85rem",
            lineHeight: 1.45,
          }}
        >
          <i
            className="las la-info-circle me-1"
            style={{ color: palette.accent }}
            aria-hidden="true"
          />
          {t("salonBooking.success.notice", {
            phone: salon?.phone || "",
          })}
        </div>

        {/* Actions */}
        <div className="d-flex flex-column gap-2">
          {salon?.phone ? (
            <a
              href={`tel:${salon.phone.replace(/\s/g, "")}`}
              className="w-100 fw-600 text-center"
              style={{
                background: "transparent",
                color: palette.accent,
                border: `1.5px solid ${palette.accent}`,
                borderRadius: 12,
                padding: "10px 14px",
                textDecoration: "none",
                fontSize: "0.95rem",
              }}
            >
              <i className="las la-phone me-2" aria-hidden="true" />
              {t("salonBooking.success.callSalon")}
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => {
              onClose?.();
              if (typeof onNewBooking === "function") onNewBooking();
            }}
            className="w-100 fw-700"
            style={{
              background: palette.accent,
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "12px 16px",
              cursor: "pointer",
              fontSize: "0.95rem",
            }}
          >
            <i className="las la-plus me-2" aria-hidden="true" />
            {t("salonBooking.success.newBooking")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
